import {
  Center,
  Loader,
  Paper,
  ScrollArea,
  Tabs,
  Text,
  Drawer,
  ActionIcon,
  Group,
  Box,
} from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import classes from './PatientPage.module.css';
import {
  PatientPageTabs,
  formatPatientPageTabUrl,
  getTabFromLocation,
} from './PatientPage.utils';
import { PatientDemographicsAndActions } from '../ainigma/patient/demographics-actions/PatientDemographicsAndActions';
import { useTranslation } from 'react-i18next';
import { PatientInfoBanner } from './PatientInfoBanner';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useCurrentEncounter } from '@hooks/useCurrentEncounter';
import { Patient } from '@medplum/fhirtypes';

interface PatientPageContentProps {
  patient: Patient;
  onPatientUpdate: (patient: Patient) => void;
}

export const PatientPageContent = ({
  patient,
  onPatientUpdate,
}: PatientPageContentProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['patient-pages']);
  const [currentTab, setCurrentTab] = useState<string>(() => {
    return (getTabFromLocation(location) ?? PatientPageTabs[0]).id;
  });
  const isTablet = useMediaQuery('(orientation: portrait)');
  const [demographicsModalInfoOpened, setDemographicsModalInfoOpened] =
    useState(false);

  // Get current encounter for the patient
  const { currentEncounter, reload: reloadEncounter } =
    useCurrentEncounter(patient);

  // Scroll detection for showing patient name in header
  const [showPatientNameInHeader, setShowPatientNameInHeader] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      if (bannerRef.current && scrollContainer) {
        const bannerRect = bannerRef.current.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Detection logic
        const isScrolledOut = bannerRect.bottom < containerRect.top + 50;
        setShowPatientNameInHeader(isScrolledOut);
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);

      handleScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isTablet, patient]);

  /**
   * Handles a tab change event.
   * @param newTabName - The new tab name.
   */
  const onTabChange = useCallback(
    (newTabName: string | null): void => {
      if (!patient?.id) {
        console.error('Not within a patient context');
        return;
      }

      const tab = newTabName
        ? PatientPageTabs.find((t) => t.id === newTabName)
        : PatientPageTabs[0];
      if (tab) {
        setCurrentTab(tab.id);
        navigate(formatPatientPageTabUrl(patient.id, tab));
      }
    },
    [navigate, patient?.id]
  );

  // Rectify the active tab UI with the current URL. This is necessary because the active tab can be changed
  // in ways other than clicking on a tab in the navigation bar.
  useEffect(() => {
    if (patient) {
      const newTab = getTabFromLocation(location);
      if (newTab && newTab.id !== currentTab) {
        setCurrentTab(newTab.id);
      }
    }
  }, [location, patient, currentTab]);

  const { ref: stickyHeaderRef, height: stickyHeaderHeight } = useElementSize();

  const {
    viewportRef,
    tabListRef,
    showScrollButtons,
    scrollLeft,
    scrollRight,
  } = useScrollOverflow();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (stickyHeaderRef.current) {
        const _ = stickyHeaderRef.current.offsetHeight;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [showPatientNameInHeader, isTablet, stickyHeaderRef]);

  return (
    <>
      <Box
        className={classes.pageWrapper}
        style={{ '--sticky-header-height': `${stickyHeaderHeight}px` } as any}
      >
        {isTablet ? (
          <Box className={classes.scrollableContent} ref={scrollContainerRef}>
            <Box ref={bannerRef}>
              <PatientInfoBanner
                patient={patient}
                onPatientUpdate={onPatientUpdate}
                showDemographicsButton={true}
                onOpenDemographics={() => setDemographicsModalInfoOpened(true)}
                currentEncounter={currentEncounter}
              />
            </Box>
            <Box className={classes.stickyHeader} ref={stickyHeaderRef}>
              <Paper p="md">
                {showPatientNameInHeader && (
                  <PatientInfoBanner
                    patient={patient}
                    onPatientUpdate={onPatientUpdate}
                    showDemographicsButton={true}
                    onOpenDemographics={() =>
                      setDemographicsModalInfoOpened(true)
                    }
                    currentEncounter={currentEncounter}
                    minimizedView={true}
                  />
                )}
                <Group gap="xs" wrap="nowrap">
                  {showScrollButtons && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={scrollLeft}
                    >
                      <IconChevronLeft />
                    </ActionIcon>
                  )}
                  <ScrollArea
                    type="hover"
                    scrollbarSize={0}
                    offsetScrollbars
                    viewportRef={viewportRef}
                    flex={1}
                  >
                    <Tabs value={currentTab} onChange={onTabChange}>
                      <Tabs.List
                        justify="center"
                        grow
                        className={classes.tabsList}
                        ref={tabListRef}
                      >
                        {PatientPageTabs.map((tab) => (
                          <Tabs.Tab key={tab.id} value={tab.id}>
                            {t(`summaryPage.tabs.${tab.id}` as any)}
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                    </Tabs>
                  </ScrollArea>
                  {showScrollButtons && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={scrollRight}
                    >
                      <IconChevronRight />
                    </ActionIcon>
                  )}
                </Group>
              </Paper>
            </Box>
            <Box
              key={getReferenceString(patient)}
              className={classes.container}
            >
              {!isTablet && <PatientDemographicsAndActions patient={patient} />}
              <Box className={classes.content}>
                <Suspense
                  fallback={
                    <Center p="xl">
                      <Loader />
                    </Center>
                  }
                >
                  <Outlet />
                </Suspense>
              </Box>
            </Box>
          </Box>
        ) : (
          // Desktop/Mobile layout: Banner stays fixed above tabs
          <>
            <Box ref={bannerRef}>
              <PatientInfoBanner
                patient={patient}
                onPatientUpdate={onPatientUpdate}
              />
            </Box>
            <Box className={classes.stickyHeader} ref={stickyHeaderRef}>
              <Paper>
                <Group gap="xs" wrap="nowrap">
                  {showScrollButtons && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={scrollLeft}
                    >
                      <IconChevronLeft />
                    </ActionIcon>
                  )}
                  <ScrollArea
                    type="hover"
                    scrollbarSize={0}
                    offsetScrollbars
                    viewportRef={viewportRef}
                    flex={1}
                  >
                    <Tabs value={currentTab} onChange={onTabChange}>
                      <Tabs.List
                        justify="center"
                        grow
                        className={classes.tabsList}
                        ref={tabListRef}
                      >
                        {PatientPageTabs.map((tab) => (
                          <Tabs.Tab key={tab.id} value={tab.id}>
                            {t(`summaryPage.tabs.${tab.id}` as any)}
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                    </Tabs>
                  </ScrollArea>
                  {showScrollButtons && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={scrollRight}
                    >
                      <IconChevronRight />
                    </ActionIcon>
                  )}
                </Group>
              </Paper>
            </Box>
            <Box className={classes.scrollableContent} ref={scrollContainerRef}>
              <Box
                key={getReferenceString(patient)}
                className={classes.container}
              >
                <PatientDemographicsAndActions patient={patient} />
                <Box className={classes.content}>
                  <Suspense
                    fallback={
                      <Center p="xl">
                        <Loader />
                      </Center>
                    }
                  >
                    <Outlet />
                  </Suspense>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Info Banner Drawer */}
      <Drawer
        onClose={() => {
          setDemographicsModalInfoOpened(false);
          reloadEncounter();
        }}
        title={
          <Text fw={600}>
            {t('summaryPage.summary.patientProfile.title', {
              ns: 'patient-pages',
              defaultValue: 'Patient Profile',
            })}
          </Text>
        }
        opened={demographicsModalInfoOpened}
        position="left"
        size="s"
      >
        <PatientDemographicsAndActions patient={patient} />
      </Drawer>
    </>
  );
};
